import {
  EntityStateDTO,
  SetupTemperatureDTO,
  TemperatureType,
} from 'business/models/Models';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import TextInput from 'components/customComponents/Experiment/ImagingSchedule/TextInput';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import Table from './scoreTable';
import './setup.css';
import { getNewId } from 'utils/Global';
import { TempConfigContext } from '../SetupDashboard';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import ExperimentService from 'business/services/ExperimentService';
import { RMModal } from 'components/modal';
import { ModalDetails } from 'business/models/CustomModels';

export const IncubationTemperature = memo(() => {
  const { incubationTemp, setIncubationTemp } = useContext(TempConfigContext);

  const [values, setValues] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
  const [selectionList, setSelectionList] = useState<boolean[]>(
    new Array(incubationTemp.length).fill(false),
  );
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    noButtonText: undefined,
    showButtons: true,
  });
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);

  const getTableColumns: object[] = [
    {
      Header: (
        <RMCheckBoxY
          isChecked={isAllChecked}
          onChange={() => onClickIsAllChecked(!isAllChecked)}
        />
      ),
      accessor: 'id',
      className: 'check-box-table',
    },
    {
      Header: (<span id="scoresettings-header-temperature">
        Temperature (*C)
      </span>),
      accessor: 'temperature',
    },
  ];

  useEffect(() => {
    setupTable();
  }, [incubationTemp, selectionList]);

  const handleOnRowClick = (row) => {
    setSelectedRowIndex(row.index);
    setSelectionList((selectionList) =>
      selectionList?.map((_, index) => index === row.index),
    );
  };

  const setupTempTable = useCallback(() => {
    if (values !== null) {
      return (
        <Table
          columns={getTableColumns}
          data={values}
          onRowClick={handleOnRowClick}
          selectedRowIndex={selectedRowIndex}
        />
      );
    }
    return null;
  }, [values, selectedRowIndex]);

  const setupTable = useCallback(() => {
    const tableData = incubationTemp.map((temp, index) => {
      selectionList[index] = selectionList[index] ? selectionList[index] : false;
      const entry = {
        id: (
          <RMCheckBoxY
            isChecked={selectionList[index]}
            onChange={() => onClickSelection(!selectionList[index], index)}
          />
        ),
        temperature: (
          <TextInput
            type="text"
            value={temp.temperature}
            id={`incubationtemperature-table-input-${temp.id}`}
            rowData={temp}
            onChange={onchangeTemperature}
            index={index}
          />
        ),
      };
      return entry;
    });
    setValues(tableData);
  }, [incubationTemp, selectionList]);

  const onchangeTemperature = useCallback(
    ({ value, index }) => {
      const experimentTemperature = [...incubationTemp];
      experimentTemperature[index].temperature = value;
      experimentTemperature[index].entityState = EntityStateDTO.Modified;
      setIncubationTemp(experimentTemperature);
    },
    [incubationTemp],
  );

  const onClickIsAllChecked = useCallback(
    (value: boolean) => {
      setSelectionList(new Array(selectionList.length).fill(value));
      setIsAllChecked(value);
    },
    [selectionList],
  );

  const onClickSelection = useCallback(
    (value: boolean, index: number) => {
      const tempSelectionList = [...selectionList];
      tempSelectionList[index] = value;
      setSelectionList(tempSelectionList);
      tempSelectionList.some((item) => item === false)
        ? setIsAllChecked(false)
        : setIsAllChecked(true);
    },
    [selectionList],
  );

  const addNewConfig = useCallback(() => {
    const newItem = new SetupTemperatureDTO({
      id: getNewId(),
      temperature: '0',
      ischilled: false,
      entityState: EntityStateDTO.New,
    });
    const newList = incubationTemp.concat(newItem);
    const tempSelectionList = [...selectionList];
    setIncubationTemp(newList);
    const newSelectionList = tempSelectionList.concat(false);
    setSelectionList(newSelectionList);
  }, [incubationTemp, selectionList]);

  const deleteConfig = useCallback(async () => {
    const selectedTemperatures = incubationTemp.filter(
      (item, index) => selectionList[index] === true,
    );
    const ids: number[] = selectedTemperatures.map((temp) => {
      return temp.id;
    });
    if (ids.length > 0) {
      const expService = new ExperimentService();
      const isInUse = await expService.isTemperatureInUse(
        ids,
        TemperatureType.Incubation,
      );
      if (isInUse === true) {
        setModalDetails({
          ...modalDetails,
          isOkOnly: true,
          showModal: true,
          onOk: () => setModalDetails({ ...modalDetails, showModal: false }),
          body: 'You cannot delete this temperature because it is currently in use.',
          heading: 'Warning',
          okButtonText: 'Ok',
        });
        return;
      }
    }

    const newSelectedList = incubationTemp.filter(
      (item, index) => selectionList[index] !== true,
    );
    if (newSelectedList.length > 0) {
      setIncubationTemp(newSelectedList);
      setSelectionList(new Array(newSelectedList.length).fill(false));
    } else {
      setIncubationTemp([]);
      setSelectionList([]);
      setIsAllChecked(false);
    }
  }, [incubationTemp, selectionList]);

  return (
    <div id='incubationtemperature-wrapper' className="float-left p-0 pt-1 incubation-table-container">
      <div id="incubationtemperature-header" className="header-container flex">
        <label id="incubationtemperature-label" className="m-2" title={'Incubation Temperature'}>
          Incubation Temperature
        </label>
        <div id="incubationtemperature-action-buttons" className="ml-auto flex">
          <div id="incubationtemperature-add-button" className="p-1 cursor-pointer" onClick={(e) => addNewConfig()}>
            <RMSvgIcon
              id="incubationtemperature-icon-add"
              name="Add"
              width={'28px'}
              height={'28px'}
              fill="#474747"
              subfolder="Setup"
            ></RMSvgIcon>
          </div>
          <div id="incubationtemperature-remove-button" className="p-1 cursor-pointer" onClick={(e) => deleteConfig()}>
            <RMSvgIcon
              id="incubationtemperature-icon-remove"
              name="Remove"
              width={'28px'}
              height={'28px'}
              opacity={selectionList.filter((i) => i === true).length ? 1 : 0.5}
              fill="#474747"
              subfolder="Setup"
            ></RMSvgIcon>
          </div>
        </div>
      </div>
      <div id="incubationtemperature-table-wrapper" className="setup-table-parent">
        <div id="incubationtemperature-table" className="incubation-table">{setupTempTable()}</div>
      </div>
      <RMModal id="incubationtemperature-modal" {...modalDetails} />
    </div>
  );
});
